import React from "react";
import { DynamicDropdown, FileDownloader, StaticDropdown } from "src/Components";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import CoveredSummaryTableUI from "src/Pages/IMEPortfolio/CoveredSummary/CoveredSummaryTableUI";
import { getPortfolioGroupInstrumentOptions } from "../portfolioGroupInstrumentOptions";
import {
  getPortfoCoverageDetailTable,
  getPortfoCoverageExcel,
  getPortfoCoverageTable,
} from "src/Requests/portfolioGroups";
import type {
  IPortfoCoverageDetailFooter,
  IPortfoCoverageDetailSearchParams,
  IPortfoCoverageDetailTableReport,
  IPortfoCoverageSearchParams,
  IPortfoCoverageTableReport,
} from "src/Interfaces/portfolioGroups";
import { Header, Report } from "src/Interfaces/TableInterfaces";
import PortfolioFilterMultiSelects from "../PortfolioFilterMultiSelects";
import { portfolioGroupExchangeTypeOptions } from "src/Utils/exchangeTypeOptions";

const NON_DETAIL_HEADERS = ["Row"];

const CoverageSummary = () => {
  const prevKeyRef = React.useRef<string | null>(null);
  const [searchParams, setSearchParams] =
    React.useState<IPortfoCoverageSearchParams>({
      portfoGroupIds: [],
      accountIds: [],
      instrumentId: null,
      exchangeType: null,
    });
  const [selectedRowId, setSelectedRowId] = React.useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = React.useState<string | null>(
    null,
  );
  const [selectedInstrument, setSelectedInstrument] = React.useState<
    string | null
  >(null);
  const [detailSearchParams, setDetailSearchParams] =
    React.useState<IPortfoCoverageDetailSearchParams | null>(null);
  const [detailFooter, setDetailFooter] =
    React.useState<IPortfoCoverageDetailFooter | null>(null);

  const filterChangeHandler = (name: string, value: number | string) => {
    setSearchParams((prev) => ({
      ...prev,
      [name]:
        value === "" || value == null
          ? null
          : name === "instrumentId"
            ? String(value)
            : Number(value),
    }));
    prevKeyRef.current = null;
    setSelectedRowId(null);
    setSelectedUserName(null);
    setSelectedInstrument(null);
    setDetailFooter(null);
    setDetailSearchParams(null);
  };

  const multiSelectFilterChangeHandler = (
    name: "portfoGroupIds" | "accountIds",
    value: string[],
  ) => {
    setSearchParams((prev) => ({ ...prev, [name]: value.map(Number) }));
    prevKeyRef.current = null;
    setSelectedRowId(null);
    setSelectedUserName(null);
    setSelectedInstrument(null);
    setDetailFooter(null);
    setDetailSearchParams(null);
  };

  const summaryCellClickHandler = (
    row: IPortfoCoverageTableReport,
    header: Header,
  ) => {
    if (NON_DETAIL_HEADERS.includes(header.headerName)) return;

    const instrument = header.groupName;
    if (!instrument) return;

    const key = `${row.id}-${instrument}`;
    if (prevKeyRef.current === key) return;
    prevKeyRef.current = key;

    setSelectedRowId(row.id);
    setSelectedUserName(row.name ?? null);
    setSelectedInstrument(instrument);
    setDetailFooter(null);
    setDetailSearchParams({
      userId: row.id,
      instrumentId: instrument,
    });
  };

  const handleDetailResponse = (
    response: Report<IPortfoCoverageDetailTableReport>,
  ) => {
    const first = response.footer?.[0] as
      | (IPortfoCoverageDetailTableReport & IPortfoCoverageDetailFooter)
      | undefined;
    setDetailFooter(
      first ? { userName: first.userName, ticker: first.ticker } : null,
    );
  };

  return (
    <div className="flex flex-col flex-1 w-full gap-3">
      <div className="flex flex-row justify-between items-end gap-3">
        <div className="flex flex-row justify-start items-end gap-3">
          <PortfolioFilterMultiSelects
            portfoGroupIds={searchParams.portfoGroupIds ?? []}
            accountIds={searchParams.accountIds ?? []}
            onChange={multiSelectFilterChangeHandler}
          />
          <div className="w-[220px]">
            <StaticDropdown
              dropdown={{
                name: "exchangeType",
                placeholder: "بورس",
                extraLabel: true,
              }}
              options={portfolioGroupExchangeTypeOptions}
              onChange={filterChangeHandler}
              value={searchParams.exchangeType?.toString() ?? ""}
            />
          </div>
          <div className="w-[220px]">
            <DynamicDropdown
              dropdown={{
                name: "instrumentId",
                placeholder: "نماد پایه",
                extraLabel: true,
              }}
              getData={getPortfolioGroupInstrumentOptions}
              onChange={filterChangeHandler}
              value={searchParams.instrumentId ?? ""}
            />
          </div>
          <div className="w-36">
            <FileDownloader getFile={() => getPortfoCoverageExcel(searchParams)} />
          </div>
        </div>
      </div>
      <div className="w-full min-w-0">
        <TableTemplate<IPortfoCoverageSearchParams, IPortfoCoverageTableReport>
          getDataTable={getPortfoCoverageTable}
          searchParameters={searchParams}
          TableUI={({ reports, onAction, sortingType, setSortingType }) => (
            <CoveredSummaryTableUI
              reports={reports}
              onAction={onAction}
              sortingType={sortingType}
              setSortingType={setSortingType}
              onCellClick={summaryCellClickHandler}
              isCellActive={(row, header) =>
                row.id === selectedRowId &&
                !!header.groupName &&
                header.groupName === selectedInstrument
              }
              tableBoxClassName="h-[calc(75vh-10.5rem)] min-h-[360px] overflow-auto portfolio-group-first-row portfolio-coverage-summary-table portfolio-summary-thick-top-border portfolio-summary-reduced-top-border"
              headersWithoutPairSeparator={["CommodityAccountName"]}
            />
          )}
          refreshTableTime={30000}
          pagesize={200}
          withSort
          tableWrapperClassname="!mx-0"
        />
      </div>
      {detailSearchParams ? (
        <>
          <div className="flex min-h-8 items-center gap-6">
            <span className="text-sm font-bold">
              نام کاربر: {selectedUserName ?? detailFooter?.userName ?? "-"}
            </span>
            <span className="text-sm font-bold">
              نماد پایه: {detailFooter?.ticker ?? selectedInstrument ?? "-"}
            </span>
          </div>

          <div className="w-fit self-center">
            <TableTemplate<
              IPortfoCoverageDetailSearchParams,
              IPortfoCoverageDetailTableReport
            >
              key={`${detailSearchParams.userId}-${detailSearchParams.instrumentId}`}
              getDataTable={getPortfoCoverageDetailTable}
              searchParameters={detailSearchParams}
              onResponse={handleDetailResponse}
              withSort
              TableUI={({ reports, onAction, sortingType, setSortingType }) => (
                <CommonTableUI
                  reports={reports}
                  onAction={onAction}
                  sortingType={sortingType}
                  setSortingType={setSortingType}
                  tableBoxClassName="h-[calc(20vh-3rem)] min-h-[140px] overflow-auto"
                />
              )}
              tableWrapperClassname="!mx-0"
            />
          </div>
        </>
      ) : null}
    </div>
  );
};

export default CoverageSummary;
